import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ソフトウェア更新中断リクエストAPI
 */
export const useSoftwareUpdateAbortAPI = (): {
  state: APIState;
  postSoftwareUpdateAbort: (vehicleId: string) => Promise<void>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postSoftwareUpdateAbortAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
    ): Promise<Result<void, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/software/update/abort`,
        );
        return new Success(undefined);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postSoftwareUpdateAbort = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_software_update_abort.failed'),
          });
          setState('hasError');
          return;
        }
        setState('loading');
        const res = await postSoftwareUpdateAbortAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
        );
        if (res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_software_update_abort.failed')}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return;
        }
        notifySuccess({
          message: t('api.fms.post_software_update_abort.successfully'),
        });
        setState('hasValue');
      },
      [notifyError, notifySuccess, postSoftwareUpdateAbortAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    postSoftwareUpdateAbort,
  };
};
